import { Footer } from '@/features/landing/sections/Footer'
import { Nav } from '@/features/landing/sections/Nav'
import { ClosingCTA } from '@/features/marketing/components/ClosingCTA'
import { ExploreHero } from '@/features/marketing/components/ExploreHero'
import { FeatureBlock } from '@/features/marketing/components/FeatureBlock'
import { MockupFrame } from '@/features/marketing/components/MockupFrame'
import { AvailabilityMockup } from '@/features/marketing/mockups/AvailabilityMockup'
import { DiscordCardMockup } from '@/features/marketing/mockups/DiscordCardMockup'
import { EventsListMockup } from '@/features/marketing/mockups/EventsListMockup'
import { MembersListMockup } from '@/features/marketing/mockups/MembersListMockup'
import { ReportMockup } from '@/features/marketing/mockups/ReportMockup'
import { SquadronHeaderMockup } from '@/features/marketing/mockups/SquadronHeaderMockup'

/**
 * /explore/squadrons — squadron-management deep-dive.
 *
 * Six-ish feature blocks. Each shows a real-looking mockup of the
 * actual product surface, with copy that names the differentiator
 * crisply. Tone matches the existing Landing — punchy, community-
 * voiced, no enterprise-speak (per MARKETING_EXPLORE_PLAN §10).
 *
 * Feature order chosen to track a squadron leader's mental funnel:
 *   1. Tier + member cap   → "what do I get?"
 *   2. Roster              → "how do I run my members?"
 *   3. Member fees         → "can I sustain this?"
 *   4. Discord bot         → "does it fit my stack?"
 *   5. Monthly report      → "what do I share with my members?"
 */
export default function ExploreSquadrons() {
  return (
    <div className="min-h-dvh bg-bg text-fg">
      <Nav />
      <main>
        <ExploreHero
          bucketLabel="Clan / Guild management"
          headline="Same person. Three handles. One roster."
          subline="Your members are a Discord tag, a Steam ID and a Twitch channel, and nothing connects them. They link their accounts once, and the roster starts showing people instead of three strangers who happen to raid together."
          secondaryCtaLabel="See tournaments"
          secondaryCtaTo="/explore/tournaments"
          visual={
            <MockupFrame address="e404.app / squadron" className="max-w-md">
              <SquadronHeaderMockup />
            </MockupFrame>
          }
        />

        <FeatureBlock
          eyebrow="01 · Roster"
          headline="A roster, not a nickname list."
          copy={
            <>
              Every member, every role, every last-seen. Promote, demote,
              ban with a reason, remove with a type-to-confirm. Verified
              military members carry a Shield pill so prospects know who
              they're joining. Live-now dots so you can see who's online
              without leaving the page.
            </>
          }
          bullets={[
            'Owner / Admin / Member roles enforced everywhere — not just labels',
            'Military verification via MOD email OTP — no manual checks',
            'Filter + search by callsign, station, joined date',
          ]}
          visual={
            <MockupFrame address="e404.app / squadron / members" className="max-w-lg">
              <MembersListMockup rowCount={7} />
            </MockupFrame>
          }
        />

        <FeatureBlock
          eyebrow="02 · Events"
          headline={
            <>
              Tournaments, sessions,<br />
              meetups, streams.<br />
              One feed.
            </>
          }
          copy={
            <>
              The whole event calendar lives in one place. Spin up a
              casual Apex squad ranked-push for tonight, schedule a
              weekly recurring CS2 session, post a Friday-night bracket
              tournament, or announce the next pub meet. Every event
              type sits next to the others so members open one app to
              see everything happening this week.
            </>
          }
          bullets={[
            'Four event types: tournaments · gaming sessions · in-person meetups · stream announcements',
            'Recurring events for the weekly fixtures (Tuesday Apex, Wednesday CS2, etc.)',
            'Per-event RSVP + check-in so you know who actually shows up',
            'Live-status pills auto-flip events to LIVE the moment they start',
          ]}
          visual={
            <MockupFrame address="e404.app / events" className="max-w-lg">
              <EventsListMockup rowCount={4} />
            </MockupFrame>
          }
          side="image-right"
        />

        <FeatureBlock
          eyebrow="03 · Player availability"
          headline={
            <>
              Know exactly who's<br />
              free to play.
            </>
          }
          copy={
            <>
              Every member sets the weekly hours they're around to play.
              You see the squadron-wide heat-map and know exactly when
              to schedule the next match — the time slot with seventeen
              of your members free leaps off the page. No more
              proposing Friday night and discovering half the squad's
              kids have football practice.
            </>
          }
          bullets={[
            'Per-game availability — different windows for Counter-Strike vs. Apex vs. casual co-op',
            'Auto-suggests the peak window when you create a new event',
            'Top-game read-out shows what your squadron plays, not what you think they play',
            'Members update from the mobile app in seconds; no spreadsheets',
          ]}
          visual={
            <MockupFrame address="e404.app / squadron / availability" className="max-w-lg">
              <AvailabilityMockup />
            </MockupFrame>
          }
        />

        <FeatureBlock
          eyebrow="04 · Discord bot"
          headline="Lives where your squadron already lives."
          copy={
            <>
              One-click connect. The bot announces new events to your
              chosen channel, syncs squadron roles to Discord roles, and
              posts tournament results when matches close. Your community
              stays in Discord; e404 keeps the receipts.
            </>
          }
          bullets={[
            'Channel picker — point at any text channel in your guild',
            'Role mirror — Admin in e404 = your designated Discord admin role',
            'No "another app to install" friction for your members',
          ]}
          visual={
            <MockupFrame address="e404.app / squadron / settings" className="max-w-lg">
              <DiscordCardMockup />
            </MockupFrame>
          }
          side="image-right"
        />

        <FeatureBlock
          eyebrow="05 · Monthly report"
          headline="A PDF you can hand your council."
          copy={
            <>
              One click, every month, generates a polished PDF: member
              growth, top games, attendance figures, military-branch
              breakdown for verified squadrons. No spreadsheet exports,
              no manual screenshot stitching.
            </>
          }
          bullets={[
            '2 free generations per month per squadron',
            'Sections auto-include / exclude based on what you track',
            'Owners get a download link; co-admins can see history',
          ]}
          visual={
            <MockupFrame address="e404.app / squadron / dashboard" className="max-w-lg">
              <ReportMockup />
            </MockupFrame>
          }
        />

        {/* Comparison strip — confidence positioning. */}
        <section className="border-b border-border/40 bg-surface/30 px-6 py-20">
          <div className="mx-auto max-w-5xl">
            <p className="font-display text-center text-xs font-bold tracking-[0.3em] text-fg-subtle uppercase">
              How e404 stacks up
            </p>
            <h2 className="font-display mt-4 text-center text-3xl leading-tight font-bold md:text-4xl">
              The tools you're using weren't designed for squadrons.
            </h2>
            <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {[
                {
                  name: 'Discord',
                  takeaway:
                    "Great for chatting, and everyone already has it installed. Dues, a moderated roster and an event that survives contact with a Friday night are still down to you.",
                },
                {
                  name: 'Guild Manager',
                  takeaway:
                    "Built for MMO raid guilds — DKP loot, voice-attendance tracking, and applications all work well here. Ask it for a tournament bracket, a paid prize pool, or per-game ELO, and you'll come up empty-handed. It's the right call for WoW raid night; Saturday bracket night is a different job entirely.",
                },
                {
                  name: 'Patreon',
                  takeaway:
                    "Collects the fee just fine — it can't host an event, run a tournament, or link to your game library.",
                },
                {
                  name: 'Spreadsheets',
                  takeaway:
                    "Free. Brittle. The moment someone leaves the squadron, the spreadsheet rots.",
                },
              ].map((c) => (
                <div
                  key={c.name}
                  className="rounded-xl border border-border bg-bg/40 p-5"
                >
                  <p className="font-display text-[10px] font-bold tracking-[0.25em] text-fg-subtle uppercase">
                    Versus
                  </p>
                  <h3 className="font-display mt-2 text-lg font-bold text-fg">
                    {c.name}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-fg-muted">
                    {c.takeaway}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ClosingCTA
          location="squadrons"
          headline="Start your squadron in five minutes."
          subline="Free up to 10 members. No card required. Upgrade only when you're ready to charge fees or run paid tournaments."
        />
      </main>
      <Footer />
    </div>
  )
}
